import React, { useState, useRef, useEffect } from 'react';
import { FaPhoneAlt, FaTimes, FaChevronLeft, FaChevronRight, FaImages } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

// Σύνδεση feature index (στη λίστα features) με χώρο – δίπλα σε κάθε feature εμφανίζεται το κουμπί του χώρου
const FEATURE_INDEX_TO_ROOM = {
  1: 'bedroom',    // 1 υπνοδωμάτιο... → Υπνοδωμάτιο 1
  2: 'livingRoom', // Τραπεζαρία με καναπέ... → Σαλόνι
  3: 'bedroom2',   // Άνετο σαλόνι... → Υπνοδωμάτιο 2
  4: 'veranda',    // Μπαλκόνι... → Βεράντα
  5: 'kitchen',    // Πλήρως εξοπλισμένη κουζίνα... → Κουζίνα
};

const ROOM_IMAGES = {
  bedroom: [
    '/domatio.png',
    '/domatio2.png',
  ],
  livingRoom: [
    '/σ.jpg',
    '/σ2.jpg',
  ],
  bedroom2: [
    '/2.jpg',
    '/2α.jpg',
    '/2αβ.jpg',
    '/2αβγ.jpg',
    '/2αβγδ.jpg',
  ],
  kitchen: [
    '/κ1.jpg',
    '/κ.jpg',
  ],
  veranda: [
    '/819360342.jpg', 
    '/0EEEAAA0-5C26-4738-B982-12C42C6A77ED.jpeg',
    '/back.avif',
    '/β.jpg',
    
  ],
};

const bookingLinksConfig = [
  {
    id: 'phone',
    name: '+30 6983446269',
    url: 'tel:+306983446269',
    logo: <FaPhoneAlt className="text-cyan-600 text-3xl" />,
    isPhone: true,
  },
  {
    id: 'airbnb',
    name: 'Airbnb',
    logo: (
      <svg width="40" height="40" viewBox="0 0 32 32" fill="none"><path d="M16.001 4.667c-2.2 0-4.267 1.2-5.4 3.134L4.534 20.267c-1.067 1.867-1.067 4.2 0 6.067 1.067 1.867 3.067 3.066 5.267 3.066h12.398c2.2 0 4.2-1.199 5.267-3.066 1.067-1.867 1.067-4.2 0-6.067L21.4 7.801C20.267 5.867 18.2 4.667 16.001 4.667zm0 2c1.467 0 2.8.8 3.533 2.067l6.067 10.666c.733 1.267.733 2.867 0 4.134-.733 1.267-2.067 2.066-3.533 2.066H9.933c-1.466 0-2.8-.799-3.533-2.066-.733-1.267-.733-2.867 0-4.134l6.067-10.666C13.2 7.467 14.534 6.667 16.001 6.667zm0 4.666a2.667 2.667 0 100 5.334 2.667 2.667 0 000-5.334z" fill="#FF5A5F"/></svg>
    ),
  },
  {
    id: 'booking',
    name: 'Booking',
    logo: (
      <svg width="40" height="40" viewBox="0 0 48 48"><rect width="48" height="48" rx="12" fill="#003580"/><text x="50%" y="60%" textAnchor="middle" fontSize="28" fill="#fff" fontFamily="Arial,Helvetica,sans-serif" fontWeight="bold">B</text></svg>
    ),
  },
  {
    id: 'yourRentals',
    name: 'YourRentals',
    logo: (
      <svg width="40" height="40" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#00B4A0"/><text x="50%" y="60%" textAnchor="middle" fontSize="16" fill="#fff" fontFamily="Arial,Helvetica,sans-serif" fontWeight="bold">YR</text></svg>
    ),
  },
];

const House = ({ getAirbnbUrl, getBookingUrl, getYourRentalsUrl, openInNewTab }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [galleryRoom, setGalleryRoom] = useState(null); // room id ή null
  const [lightboxIndex, setLightboxIndex] = useState(null); // index φωτό στη lightbox ή null
  const dropdownRef = useRef(null);
  const { t } = useLanguage();

  const getUrlFor = (id) => {
    if (id === 'airbnb' && getAirbnbUrl) return getAirbnbUrl();
    if (id === 'booking' && getBookingUrl) return getBookingUrl();
    if (id === 'yourRentals' && getYourRentalsUrl) return getYourRentalsUrl();
    return '#';
  };
  const bookingLinks = bookingLinksConfig;

  const roomPhotos = galleryRoom ? (ROOM_IMAGES[galleryRoom] || []) : [];
  const closeGallery = () => { setGalleryRoom(null); setLightboxIndex(null); };
  const openLightbox = (idx) => setLightboxIndex(idx);
  const lightboxPrev = () => setLightboxIndex((i) => (i <= 0 ? roomPhotos.length - 1 : i - 1));
  const lightboxNext = () => setLightboxIndex((i) => (i >= roomPhotos.length - 1 ? 0 : i + 1));
  // Άνοιγμα γκαλερί χώρου με την πρώτη φωτό να εμφανίζεται αμέσως (lightbox)
  const openRoomGallery = (roomId) => {
    setGalleryRoom(roomId);
    setLightboxIndex(0);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Κλείσιμο γκαλερί με Escape (πρώτα lightbox, μετά γκαλερί), lock scroll όταν ανοιχτό
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (lightboxIndex !== null) setLightboxIndex(null);
      else { setGalleryRoom(null); setLightboxIndex(null); }
    };
    if (galleryRoom || lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [galleryRoom, lightboxIndex]);

  return (
    <section className="flex flex-col md:flex-row w-full min-h-screen bg-white justify-center">
      {/* Εικόνα αριστερά - μεγαλύτερη και καλύτερη */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gray-100 p-0 m-0 relative overflow-hidden min-h-[250px] md:min-h-0 h-64 md:h-auto">
        <img 
          src="/819360342.jpg" 
          alt="Azure Loft House" 
          className="object-cover w-full h-full min-h-[250px] md:min-h-[500px]" 
        />
        {/* Overlay για καλύτερη εμφάνιση */}
        <div className="absolute inset-0 bg-black/10"></div>
      </div>
      {/* Περιγραφή δεξιά - μεγαλύτερη και καλύτερη */}
      <div className="w-full md:w-1/2 flex flex-col justify-center p-4 sm:p-8 md:p-20 xl:p-24 relative overflow-hidden min-h-[400px] md:min-h-[600px]"
           style={{ 
             backgroundImage: 'url(/blue-back.avif)', 
             backgroundSize: 'cover', 
             backgroundPosition: 'center',
             backgroundAttachment: 'fixed'
           }}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-cyan-900/20 z-0" />
        <div className="relative z-10">
          <div className="bg-white/50 backdrop-blur-lg border border-white/30 rounded-2xl p-4 sm:p-8 md:p-16 xl:p-20 shadow-2xl">
            <h2 className="text-3xl sm:text-5xl xl:text-7xl font-extrabold mb-6 sm:mb-10 text-black drop-shadow-lg font-['Montserrat','Poppins',sans-serif] tracking-tight leading-tight">
              {t('house.title')}
            </h2>
            <p className="text-lg sm:text-xl xl:text-3xl mb-6 sm:mb-10 text-black font-semibold font-['Montserrat','Poppins',sans-serif] drop-shadow-lg leading-relaxed">
              {t('house.description')}
            </p>
            <ul className="mb-8 sm:mb-12 text-base sm:text-xl xl:text-2xl text-black font-medium list-disc list-inside drop-shadow-lg font-['Montserrat','Poppins',sans-serif] space-y-2 sm:space-y-3">
              {t('house.features').map((feature, index) => {
                const isSectionHeader = [
                  'Χώροι Διαμονής',
                  'Κουζίνα & Παροχές',
                  'Ανέσεις & Επιπλέον Παροχές',
                  'Spaces',
                  'Kitchen & Facilities',
                  'Amenities & Extras',
                  'Räumlichkeiten',
                  'Küche & Ausstattung',
                  'Annehmlichkeiten & Extras',
                  'Espaces',
                  'Cuisine & Équipements',
                  'Commodités & Extras',
                  'Spazi',
                  'Cucina & Servizi',
                  'Comfort & Extra'
                ].includes(feature.trim());
                const roomId = FEATURE_INDEX_TO_ROOM[index];
                const roomLabel = roomId ? t(`house.rooms.${roomId}`) : null;
                return (
                  <li
                    key={index}
                    className={
                      isSectionHeader
                        ? 'mt-4 sm:mt-6 mb-1 sm:mb-2 text-xl sm:text-3xl xl:text-4xl font-extrabold text-amber-700 font-[\'Playfair Display\',\'Cormorant Garamond\',serif] tracking-tight text-center list-none'
                        : 'leading-relaxed font-\'Montserrat\' font-medium flex flex-wrap items-center gap-2 sm:gap-3'
                    }
                    style={isSectionHeader ? { fontFamily: 'Playfair Display, Cormorant Garamond, Georgia, serif', fontWeight: 800, letterSpacing: '0.02em' } : { fontFamily: 'Montserrat, Poppins, Inter, sans-serif', fontWeight: 500 }}
                  >
                    <span className={roomId ? 'flex-1 min-w-0' : undefined}>{feature}</span>
                    {roomLabel && (
                      <button
                        type="button"
                        onClick={() => openRoomGallery(roomId)}
                        className="inline-flex items-center gap-1.5 flex-shrink-0 bg-amber-800 hover:bg-amber-700 text-white font-semibold px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow hover:shadow-md transition-all duration-200 text-sm sm:text-base"
                        style={{ fontFamily: 'Montserrat, Poppins, sans-serif' }}
                      >
                        <FaImages className="text-white flex-shrink-0" size={14} />
                        {roomLabel}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="flex justify-center">
              <div className="relative" ref={dropdownRef}>
                <button
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 sm:px-10 md:px-16 py-4 sm:py-6 md:py-8 font-bold rounded-full shadow-2xl hover:scale-105 hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 text-xl sm:text-2xl md:text-4xl flex items-center gap-2 sm:gap-4 transform hover:shadow-3xl"
                  onClick={() => setDropdownOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                >
                  {t('house.bookNow')}
                  <svg className={`ml-2 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} width="24" height="24" viewBox="0 0 24 24">
                    <path d="M7 10l5 5 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  </svg>
                </button>
                {dropdownOpen && (
                  <>
                    {/* Backdrop για mobile */}
                    <div className="fixed inset-0 bg-black/30 z-[10000] sm:hidden animate-fade-in" onClick={() => setDropdownOpen(false)} />
                    {/* Dropdown */}
                    <div
                      className="z-[10001] animate-fade-in"
                      style={{
                        position: window.innerWidth < 640 ? 'fixed' : 'absolute',
                        left: window.innerWidth < 640 ? '50%' : '50%',
                        top: window.innerWidth < 640 ? '50%' : '100%',
                        transform: window.innerWidth < 640 ? 'translate(-50%, -50%)' : 'translate(-50%, 0)',
                        width: window.innerWidth < 640 ? '90vw' : '24rem',
                        maxWidth: 400,
                        background: '#fff',
                        borderRadius: 18,
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.25)',
                        border: '1.5px solid #bae6fd',
                        padding: window.innerWidth < 640 ? 12 : 16,
                        outline: '2px solid #2563eb22',
                        ring: '2px solid #22d3ee22',
                        zIndex: 10001
                      }}
                    >
                      {bookingLinks.map((platform) => (
                        <a
                          key={platform.id}
                          href={platform.isPhone ? platform.url : getUrlFor(platform.id)}
                          target={platform.isPhone ? undefined : '_blank'}
                          rel={platform.isPhone ? undefined : 'noopener noreferrer'}
                          className="flex items-center gap-3 sm:gap-5 px-4 sm:px-8 py-4 sm:py-6 hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-200 text-lg sm:text-2xl font-semibold text-gray-800 rounded-xl mx-1 sm:mx-2 hover:scale-105"
                          onClick={(e) => {
                            if (!platform.isPhone && openInNewTab) {
                              e.preventDefault();
                              openInNewTab(getUrlFor(platform.id));
                            }
                            setDropdownOpen(false);
                          }}
                        >
                          {platform.logo}
                          <span className="font-bold">{platform.name}</span>
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal γκαλερί χώρου – grid φωτό, κλικ ανοίγει lightbox */}
      {galleryRoom && (
        <div className="fixed inset-0 z-[10002] flex flex-col bg-black/95" role="dialog" aria-modal="true" aria-label={t(`house.rooms.${galleryRoom}`)}>
          <div className="flex items-center justify-between p-4 border-b border-white/20">
            <h3 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, Poppins, sans-serif' }}>
              {t(`house.rooms.${galleryRoom}`)}
            </h3>
            <button
              type="button"
              onClick={closeGallery}
              className="p-2 rounded-full text-white hover:bg-white/20 transition-colors"
              aria-label={t('house.closeGallery')}
            >
              <FaTimes size={28} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto">
              {roomPhotos.map((src, idx) => (
                <button
                  key={`${galleryRoom}-${idx}`}
                  type="button"
                  onClick={() => openLightbox(idx)}
                  className="block w-full aspect-square rounded-xl overflow-hidden border-2 border-white/30 hover:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox – μία φωτό μεγαλή, prev/next, κλείσιμο */}
      {galleryRoom && lightboxIndex !== null && roomPhotos[lightboxIndex] && (
        <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/98" role="dialog" aria-modal="true">
          <button
            type="button"
            onClick={closeGallery}
            className="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white/20 z-10"
            aria-label={t('house.closeGallery')}
          >
            <FaTimes size={32} />
          </button>
          <button
            type="button"
            onClick={lightboxPrev}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors"
            aria-label="Previous"
          >
            <FaChevronLeft size={28} />
          </button>
          <img
            src={roomPhotos[lightboxIndex]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={lightboxNext}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors"
            aria-label="Next"
          >
            <FaChevronRight size={28} />
          </button>
        </div>
      )}
    </section>
  );
};

export default House; 